import React, { Component } from 'react';
import { Checkbox, Select, Button, Spin, message } from 'antd';

import LS_SERVICE from '../../utils/localStorage';
import API_SERVICE from '../../utils/apiService';

import {
  GET_SMART_EMR_CONFIG,
  STORE_SMART_EMR_CONFIG
} from "../../utils/constant";

const { Option } = Select;

/**
 * SmartEmrComponent
 * - Persists per-field checkbox flags to backend and reads them back on fetch.
 * - Dropdowns are enabled only when their checkbox is checked.
 * - Chief Complaint & Physical Examination are only checkboxes (no dropdowns).
 * - If a checkbox for a dropdown field is checked, that dropdown becomes mandatory on save.
 */
class SmartEmrComponent extends Component {
  state = {
    loading: false,
    saving: false,
    smartemr: 'N',

    // selections (id/name strings)
    medicine: '',
    test: '',
    diagnosis: '',
    instruction: '',
    otherdetails: '',
    physiotherapy: '',
    followup: '',
    admission: '',

    // per-field toggles stored to DB: 'Y' or 'N'
    enable_chiefcomplaint: 'N',
    enable_phys_examination: 'N',
    enable_vitals: 'N',
    enable_medicine: 'N',
    enable_test: 'N',
    enable_diagnosis: 'N',
    enable_instruction: 'N',
    enable_otherdetails: 'N',
    enable_physiotherapy: 'N',
    enable_followup: 'N',
    enable_admission: 'N',

    // validation errors for required dropdowns when enabled
    errors: {},

    customOptions: [],
    initialData: null,
  };

  controller = null;

  componentDidMount() {
    this.fetchEmr();
    this.loadCustomOptionsFromLocalStorage();
  }

  componentWillUnmount() {
    if (this.controller) this.controller.abort();
  }

  // Load dynamic custom options from localStorage
  loadCustomOptionsFromLocalStorage = () => {
    try {
      const ss_configuration = LS_SERVICE.get('ss_configuration');
      if (!ss_configuration || !Array.isArray(ss_configuration)) return;

      const result = [];

      const matchesFilter = (node) => {
        const cfAllowed = node.custom_field_allowed === 'Y';
        const cfTypeTextbox = node.custom_field_type === 'textbox';
        const cfg = node.configuration || {};
        const opdSelected = cfg.opd_screen?.selected === 'Y';
        const vcSelected = cfg.vc_screen?.selected === 'Y';
        return cfAllowed && cfTypeTextbox && (opdSelected || vcSelected);
      };

      const collect = (nodes) => {
        nodes.forEach((node) => {
          if (matchesFilter(node)) {
            const rawValue = node.custom?.value ?? node.custom_field_value ?? node.display_title ?? node.name ?? node.id;
            const value = rawValue != null ? String(rawValue) : '';
            const name = node.name ?? String(node.id ?? value);
            const display_title = node.display_title ?? node.name ?? value;
            result.push({ key: name, value, title: display_title + ": " + value, id: node.id, name });
          }
          if (Array.isArray(node.sub_items) && node.sub_items.length) {
            collect(node.sub_items);
          }
        });
      };

      collect(ss_configuration);

      // Dedupe by key (name)
      const deduped = [];
      const seen = new Set();
      for (const opt of result) {
        if (!seen.has(opt.key)) {
          seen.add(opt.key);
          deduped.push(opt);
        }
      }

      this.setState({ customOptions: deduped });
    } catch (err) {
      console.error('loadCustomOptionsFromLocalStorage error', err);
    }
  };

  // Fetch saved config including per-field enable flags
  fetchEmr = async () => {
    this.controller = new AbortController();
    const signal = this.controller.signal;

    this.setState({ loading: true });
    try {
      const smart_emr_config_res = await API_SERVICE.post(GET_SMART_EMR_CONFIG, {
        hospital_id: LS_SERVICE.get('FACILITY_ID'),
        staff_id: LS_SERVICE.get('staff_id')
      });

      const data = smart_emr_config_res.data || {};
      const {
        smartemr = 'N',
        medicine = '',
        test = '',
        diagnosis = '',
        instruction = '',
        otherdetails = '',
        physiotherapy = '',
        followup = '',
        admission = '',

        enable_chiefcomplaint = 'N',
        enable_phys_examination = 'N',
        enable_vitals = 'N',
        enable_medicine = 'N',
        enable_test = 'N',
        enable_diagnosis = 'N',
        enable_instruction = 'N',
        enable_otherdetails = 'N',
        enable_physiotherapy = 'N',
        enable_followup = 'N',
        enable_admission = 'N',
      } = data || {};

      const initialData = {
        smartemr,
        medicine,
        test,
        diagnosis,
        instruction,
        otherdetails,
        physiotherapy,
        followup,
        admission,
        enable_chiefcomplaint,
        enable_phys_examination,
        enable_vitals,
        enable_medicine,
        enable_test,
        enable_diagnosis,
        enable_instruction,
        enable_otherdetails,
        enable_physiotherapy,
        enable_followup,
        enable_admission,
      };

      LS_SERVICE.set('smart_emr_config', data || { smartemr: 'N' });

      this.setState({
        smartemr,
        medicine: medicine != null ? String(medicine) : '',
        test: test != null ? String(test) : '',
        diagnosis: diagnosis != null ? String(diagnosis) : '',
        instruction: instruction != null ? String(instruction) : '',
        otherdetails: otherdetails != null ? String(otherdetails) : '',
        physiotherapy: physiotherapy != null ? String(physiotherapy) : '',
        followup: followup != null ? String(followup) : '',
        admission: admission != null ? String(admission) : '',

        enable_chiefcomplaint,
        enable_phys_examination,
        enable_vitals,
        enable_medicine,
        enable_test,
        enable_diagnosis,
        enable_instruction,
        enable_otherdetails,
        enable_physiotherapy,
        enable_followup,
        enable_admission,

        initialData,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchEmr Error:', err);
        message.error('Unable to fetch EMR configuration.');
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  // Toggle main feature
  handleMainCheckboxChange = (e) => {
    this.setState({ smartemr: e.target.checked ? 'Y' : 'N' });
  };

  // Toggle per-field enables (these 'Y'|'N' flags are persisted to DB)
  // When enabling a dropdown, clear any previous error for that field.
  handleFieldToggle = (fieldKey, relatedFieldValueKey) => (e) => {
    const newFlag = e.target.checked ? 'Y' : 'N';
    this.setState((prev) => {
      const next = { [fieldKey]: newFlag };
      // clear validation error when enabling/disable
      const errors = { ...(prev.errors || {}) };
      delete errors[relatedFieldValueKey];
      next.errors = errors;
      return next;
    });
  };

  // normalize Select value changes and clear any validation error for that field
  handleSelectChange = (field, value) => {
    this.setState((prev) => {
      const errors = { ...(prev.errors || {}) };
      delete errors[field];
      return { [field]: value == null ? '' : String(value), errors };
    });
  };

  // Build payload: include field values ONLY if corresponding enable flag true
  buildSavePayload = () => {
    const {
      smartemr,
      medicine,
      test,
      diagnosis,
      instruction,
      otherdetails,
      physiotherapy,
      followup,
      admission,

      enable_chiefcomplaint,
      enable_phys_examination,
      enable_vitals,
      enable_medicine,
      enable_test,
      enable_diagnosis,
      enable_instruction,
      enable_otherdetails,
      enable_physiotherapy,
      enable_followup,
      enable_admission,
    } = this.state;

    return {
      hospital_id: LS_SERVICE.get('FACILITY_ID'),
      staff_id: LS_SERVICE.get('staff_id'),
      smartemr,

      // dropdown fields: send id only if enabled, else null
      medicine: enable_medicine === 'Y' ? (medicine || null) : null,
      test: enable_test === 'Y' ? (test || null) : null,
      diagnosis: enable_diagnosis === 'Y' ? (diagnosis || null) : null,
      instruction: enable_instruction === 'Y' ? (instruction || null) : null,
      otherdetails: enable_otherdetails === 'Y' ? (otherdetails || null) : null,
      physiotherapy: enable_physiotherapy === 'Y' ? (physiotherapy || null) : null,
      followup: enable_followup === 'Y' ? (followup || null) : null,
      admission: enable_admission === 'Y' ? (admission || null) : null,

      // include per-field enable flags so backend persists checkbox state
      enable_medicine,
      enable_test,
      enable_diagnosis,
      enable_instruction,
      enable_otherdetails,
      enable_chiefcomplaint,
      enable_phys_examination,
      enable_vitals,
      enable_physiotherapy,
      enable_followup,
      enable_admission,
    };
  };

  // Validate required dropdowns: if checkbox is 'Y' then corresponding field must have a value
  validateRequiredFields = () => {
    const {
      medicine,
      test,
      diagnosis,
      instruction,
      otherdetails,
      physiotherapy,
      followup,
      admission,

      enable_medicine,
      enable_test,
      enable_diagnosis,
      enable_instruction,
      enable_otherdetails,
      enable_physiotherapy,
      enable_followup,
      enable_admission,
    } = this.state;

    const errors = {};
    if (enable_medicine === 'Y' && (!medicine || medicine === '')) errors.medicine = 'Please select Medicine';
    if (enable_test === 'Y' && (!test || test === '')) errors.test = 'Please select Tests';
    if (enable_diagnosis === 'Y' && (!diagnosis || diagnosis === '')) errors.diagnosis = 'Please select Diagnosis';
    if (enable_instruction === 'Y' && (!instruction || instruction === '')) errors.instruction = 'Please select Instructions';
    if (enable_otherdetails === 'Y' && (!otherdetails || otherdetails === '')) errors.otherdetails = 'Please select Other Details';
    if (enable_physiotherapy === 'Y' && (!physiotherapy || physiotherapy === '')) errors.physiotherapy = 'Please select physiotherapy';
    if (enable_followup === 'Y' && (!followup || followup === '')) errors.followup = 'Please select follow-up';
    if (enable_admission === 'Y' && (!admission || admission === '')) errors.admission = 'Please select admission';

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  // Save payload to backend and re-fetch saved config
  handleSave = async () => {
    // validate required dropdowns before sending
    const ok = this.validateRequiredFields();
    if (!ok) {
      const { errors } = this.state;
      const firstErr = Object.values(errors)[0];
      // message.error(firstErr || 'Please fill required fields');
      return;
    }

    const payload = this.buildSavePayload();

    this.setState({ saving: true });
    try {
      await API_SERVICE.post(STORE_SMART_EMR_CONFIG, payload);
      message.success('Configuration saved');
      await this.fetchEmr();
    } catch (err) {
      console.error('handleSave Error:', err);
      message.error('Unable to save EMR configuration.');
    } finally {
      this.setState({ saving: false });
    }
  };

  // prevent duplicate selection across enabled dropdowns
  getSelectedValuesSet = () => {
    const {
      medicine,
      test,
      diagnosis,
      instruction,
      otherdetails,
      physiotherapy,
      followup,
      admission,

      enable_medicine,
      enable_test,
      enable_diagnosis,
      enable_instruction,
      enable_otherdetails,
      enable_physiotherapy,
      enable_followup,
      enable_admission,
    } = this.state;

    return new Set(
      [
        enable_medicine === 'Y' ? medicine : null,
        enable_test === 'Y' ? test : null,
        enable_diagnosis === 'Y' ? diagnosis : null,
        enable_instruction === 'Y' ? instruction : null,
        enable_otherdetails === 'Y' ? otherdetails : null,
        enable_physiotherapy === 'Y' ? physiotherapy : null,
        enable_followup === 'Y' ? followup : null,
        enable_admission === 'Y' ? admission : null,
      ]
        .filter((v) => v != null && v !== '')
        .map((v) => String(v))
    );
  };

  // render a Select for dropdown fields, disabled if corresponding checkbox not checked
  // also shows a red border when field is required and missing (simple visual feedback)
  renderSelect = (name, firstOptionValue, enabledFlag) => {
    const rawValue = this.state[name];
    const value = rawValue == null || rawValue === '' ? undefined : String(rawValue);
    const { customOptions, errors } = this.state;
    const selectedSet = this.getSelectedValuesSet();
    const disabled = this.state[enabledFlag] === 'Y' ? false : true;
    const ss_configuration = LS_SERVICE.get('ss_configuration') || [];
    const style = { width: '100%' };
    if (!disabled && errors && errors[name]) {
      // apply red border to indicate required error
      style.border = '1px solid #ff4d4f';
      style.borderRadius = 2;
    }

    return (
      <Select
        name={name}
        id={name}
        value={value}
        onChange={(val) => this.handleSelectChange(name, val)}
        style={style}
        allowClear
        placeholder={disabled ? 'Enable '+name+' to select the field' : ''}
        disabled={disabled}
      >
        {firstOptionValue != null && (
          <>
            {ss_configuration.some(item => 
              // 1. Check the parent level
              (item.name === name && (item.configuration?.opd_screen?.selected === "Y" || item.configuration?.vc_screen?.selected === "Y")) ||
              // 2. Check the sub_items level
              (item.sub_items && item.sub_items.some(sub => 
                sub.name === name && (sub.configuration?.opd_screen?.selected === "Y" || sub.configuration?.vc_screen?.selected === "Y")
              ))) && (
              <Option key={`default-${name}`} value={name}>
                {firstOptionValue}
              </Option>
            )}
          </>
        )}
        {customOptions.map((opt) => {
          const optValue = String(opt.name);
          const isCurrentValue = String(rawValue) === optValue;
          const isSelectedInOthers = selectedSet.has(optValue) && !isCurrentValue;
          return (
            <Option key={opt.key} value={optValue} title={opt.title} disabled={isSelectedInOthers}>
              {opt.title}
            </Option>
          );
        })}
      </Select>
    );
  };

  render() {
    const {
      loading,
      saving,
      smartemr,

      enable_chiefcomplaint,
      enable_phys_examination,
      enable_vitals,
      enable_medicine,
      enable_test,
      enable_diagnosis,
      enable_instruction,
      enable_otherdetails,
      enable_physiotherapy,
      enable_followup,
      enable_admission,
    } = this.state;

    return (
      <div className="container-fluid">
        <Spin spinning={loading}>
          <div className="row">
            <div className="col-12">
              <Checkbox name="smartemr" checked={smartemr === 'Y'} onChange={this.handleMainCheckboxChange}>
                Enable smart EMR
              </Checkbox>
            </div>
          </div>

          {smartemr === 'Y' && (
            <div id="smart-emr-detail" className="row mt-4">
              <div className="col-12">
                <table className="table emr_config_table">
                  <tbody>
                    {/* Chief Complaints: only checkbox, persisted to DB */}
                    <tr>
                      <td width="60%">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_chiefcomplaint === 'Y'} onChange={this.handleFieldToggle('enable_chiefcomplaint', 'chiefcomplaint')} />
                          <span>Chief Complaints</span>
                        </div>
                      </td>
                      <td width="40%"></td>
                    </tr>

                    {/* Physical Examination: only checkbox, persisted to DB */}
                    <tr>
                      <td width="60%">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_phys_examination === 'Y'} onChange={this.handleFieldToggle('enable_phys_examination', 'phys_examination')} />
                          <span>Physical Examination</span>
                        </div>
                      </td>
                      <td width="40%"></td>
                    </tr>

                    {/* Vitals: only checkbox, persisted to DB */}
                    <tr>
                      <td width="60%">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_vitals === 'Y'} onChange={this.handleFieldToggle('enable_vitals', 'vitals')} />
                          <span>Vitals</span>
                        </div>
                      </td>
                      <td width="40%"></td>
                    </tr>

                    {/* Medicine: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td width="60%">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_medicine === 'Y'} onChange={this.handleFieldToggle('enable_medicine', 'medicine')} />
                          <span>Medicine</span>
                        </div>
                      </td>
                      <td width="40%">{this.renderSelect('medicine', 'Medicine', 'enable_medicine')}</td>
                    </tr>

                    {/* Tests: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_test === 'Y'} onChange={this.handleFieldToggle('enable_test', 'test')} />
                          <span>Tests</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('test', 'Test', 'enable_test')}</td>
                    </tr>

                    {/* Diagnosis: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_diagnosis === 'Y'} onChange={this.handleFieldToggle('enable_diagnosis', 'diagnosis')} />
                          <span>Diagnosis</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('diagnosis', 'Diagnosis', 'enable_diagnosis')}</td>
                    </tr>

                    {/* Instructions: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_instruction === 'Y'} onChange={this.handleFieldToggle('enable_instruction', 'instruction')} />
                          <span>Instructions</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('instruction', 'Instruction', 'enable_instruction')}</td>
                    </tr>
                    {/* PHYSIOTHERAPY: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_physiotherapy === 'Y'} onChange={this.handleFieldToggle('enable_physiotherapy', 'physiotherapy')} />
                          <span>Physiotherapy</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('physiotherapy', 'Physiotherapy', 'enable_physiotherapy')}</td>
                    </tr>

                    {/* FOLLOW UP: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_followup === 'Y'} onChange={this.handleFieldToggle('enable_followup', 'followup')} />
                          <span>Follow Up</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('followup', 'Follow Up', 'enable_followup')}</td>
                    </tr>

                    {/* Admission Advised: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_admission === 'Y'} onChange={this.handleFieldToggle('enable_admission', 'admission')} />
                          <span>Admission Advised</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('admission', 'Admission Advised', 'enable_admission')}</td>
                    </tr>

                    {/* Other Details: checkbox + dropdown (dropdown enabled only if checkbox checked) */}
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Checkbox checked={enable_otherdetails === 'Y'} onChange={this.handleFieldToggle('enable_otherdetails', 'otherdetails')} />
                          <span>Other Details</span>
                        </div>
                      </td>
                      <td>{this.renderSelect('otherdetails', null, 'enable_otherdetails')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="primary" onClick={this.handleSave} loading={saving}>
              Save
            </Button>
          </div>
        </Spin>
      </div>
    );
  }
}

export default SmartEmrComponent;